<template>
  <div ref="rootRef" class="language-switcher" :class="variant">
    <label v-if="showLabel" :id="labelId" class="language-switcher-label">
      {{ t('language.label') }}
    </label>

    <div class="language-switcher-wrap">
      <button
        type="button"
        class="language-switcher-trigger"
        :aria-label="t('language.label')"
        :aria-expanded="isOpen"
        :aria-haspopup="true"
        :aria-labelledby="showLabel ? labelId : undefined"
        @click="toggleOpen"
        @keydown.escape="close"
      >
        <LocaleFlag :code="localeStore.locale" size="md" :title="currentOption.label" />
        <span class="language-switcher-trigger-label">{{ currentOption.label }}</span>
        <span class="language-switcher-chevron" :class="{ open: isOpen }" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
          </svg>
        </span>
      </button>

      <Transition name="lang-menu">
        <ul
          v-if="isOpen"
          class="language-switcher-menu"
          role="listbox"
          :aria-label="t('language.label')"
        >
          <li
            v-for="opt in localeOptions"
            :key="opt.code"
            role="option"
            :aria-selected="localeStore.locale === opt.code"
          >
            <button
              type="button"
              class="language-switcher-option"
              :class="{ active: localeStore.locale === opt.code }"
              @click="selectLocale(opt.code)"
            >
              <LocaleFlag :code="opt.code" size="lg" :title="opt.label" />
              <span class="language-switcher-option-label">{{ opt.label }}</span>
              <span v-if="localeStore.locale === opt.code" class="language-switcher-check" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                </svg>
              </span>
            </button>
          </li>
        </ul>
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLocaleStore } from '../stores/locale'
import LocaleFlag from './LocaleFlag.vue'

defineProps({
  variant: {
    type: String,
    default: 'default'
  },
  showLabel: {
    type: Boolean,
    default: true
  }
})

const labelId = `language-switcher-label-${Math.random().toString(36).slice(2, 9)}`
const { t } = useI18n()
const localeStore = useLocaleStore()

const rootRef = ref(null)
const isOpen = ref(false)

const localeOptions = computed(() => [
  { code: 'pt-BR', label: t('language.pt') },
  { code: 'es', label: t('language.es') },
  { code: 'en', label: t('language.en') }
])

const currentOption = computed(() => {
  return localeOptions.value.find((o) => o.code === localeStore.locale) ?? localeOptions.value[0]
})

const toggleOpen = () => {
  isOpen.value = !isOpen.value
}

const close = () => {
  isOpen.value = false
}

const selectLocale = (code) => {
  localeStore.setLocale(code)
  close()
}

const onClickOutside = (event) => {
  if (rootRef.value && !rootRef.value.contains(event.target)) {
    close()
  }
}

onMounted(() => {
  document.addEventListener('click', onClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside)
})
</script>

<style scoped>
.language-switcher {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.language-switcher.compact {
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}

.language-switcher-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-secondary);
}

.language-switcher-wrap {
  position: relative;
}

.language-switcher-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 10.25rem;
  padding: 0.45rem 0.65rem 0.45rem 0.55rem;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--border-primary) 70%, var(--info) 30%);
  background: linear-gradient(
    145deg,
    color-mix(in srgb, var(--bg-tertiary) 92%, var(--info) 8%),
    var(--bg-secondary)
  );
  color: var(--text-primary);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.12),
    0 4px 14px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.language-switcher.compact .language-switcher-trigger {
  min-width: 9.5rem;
}

.language-switcher-trigger:hover {
  border-color: color-mix(in srgb, var(--info) 55%, var(--border-hover));
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.14),
    0 8px 22px color-mix(in srgb, var(--info) 18%, transparent);
}

.language-switcher-trigger:focus-visible {
  outline: none;
  border-color: var(--info);
  box-shadow:
    0 0 0 3px color-mix(in srgb, var(--info) 28%, transparent),
    0 4px 16px color-mix(in srgb, var(--info) 22%, transparent);
}

.language-switcher-trigger-label {
  flex: 1;
  text-align: left;
  white-space: nowrap;
}

.language-switcher-chevron {
  display: flex;
  width: 1.125rem;
  height: 1.125rem;
  color: var(--text-tertiary);
  transition: transform 0.2s ease, color 0.2s ease;
}

.language-switcher-chevron.open {
  transform: rotate(180deg);
  color: var(--info);
}

.language-switcher-trigger:hover .language-switcher-chevron {
  color: var(--info);
}

.language-switcher-menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 50;
  min-width: 100%;
  margin: 0;
  padding: 0.35rem;
  list-style: none;
  border-radius: 10px;
  border: 1px solid var(--border-primary);
  background-color: var(--bg-modal);
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.2),
    0 4px 10px rgba(0, 0, 0, 0.1);
}

.language-switcher-option {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.55rem 0.65rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.language-switcher-option:hover {
  background-color: var(--bg-hover);
}

.language-switcher-option.active {
  background-color: color-mix(in srgb, var(--info) 14%, var(--bg-hover));
}

.language-switcher-option-label {
  flex: 1;
}

.language-switcher-check {
  display: flex;
  width: 1rem;
  height: 1rem;
  color: var(--info);
}

.lang-menu-enter-active,
.lang-menu-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.lang-menu-enter-from,
.lang-menu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (max-width: 480px) {
  .language-switcher.compact .language-switcher-trigger {
    min-width: 8.5rem;
  }

  .language-switcher-trigger-label {
    font-size: 0.75rem;
  }
}
</style>
